import { z } from 'zod';
import { competencia } from './centros-custo.js';
import { cuid, dinheiro, isoDate, paginacao, taxa } from './primitives.js';

/**
 * Sessao 17: plano de vendas. O XARP faz o "plano" (previsto, competencia); o "realizado" (caixa) vem do ERP/Orbit depois.
 * Dinheiro e taxa: string decimal. Quantidade na unidade de CUSTO do item (t); preco por unidade de VENDA (kg).
 */

export const mesParam = z.object({ mes: competencia });
export type MesParam = z.infer<typeof mesParam>;

// ---------- Parametros do plano ----------

export const REGRAS_FRETE_NET = ['LEGAL', 'FATOR_FIXO'] as const;
export const regraFreteNet = z.enum(REGRAS_FRETE_NET);
export type RegraFreteNet = z.infer<typeof regraFreteNet>;

export const planoParametrosSchema = z
  .object({
    regraFreteNet: regraFreteNet,
    /** Obrigatorio com FATOR_FIXO (0 < f <= 1): frete net = frete x fator. */
    fatorFreteNet: taxa.nullable(),
    freteIcmsPct: taxa,
    empresaNaoCumulativa: z.boolean(),
    /** Peso da Previsao no total do mes (padrao 1 = 100%, como a planilha; 0,9 = 90%). */
    pesoPrevisao: taxa,
    icmsPadrao: taxa,
    pisPadrao: taxa,
    cofinsPadrao: taxa,
    comissaoPadrao: taxa,
    pendenteNaNecessidade: z.boolean(),
    saldoInicialFluxo: dinheiro,
  })
  .refine((v) => v.regraFreteNet !== 'FATOR_FIXO' || (v.fatorFreteNet !== null && Number(v.fatorFreteNet) > 0 && Number(v.fatorFreteNet) <= 1), { message: 'Informe o fator do frete net (maior que 0 e até 1)', path: ['fatorFreteNet'] })
  .refine((v) => Number(v.pesoPrevisao) <= 1, { message: 'O peso da Previsão é até 100%', path: ['pesoPrevisao'] });
export type PlanoParametrosDto = z.infer<typeof planoParametrosSchema>;
export type PlanoParametrosResponse = PlanoParametrosDto & { configurado: boolean };

// ---------- Rubricas do custo fabrica ----------

export const CLASSES_RUBRICA = ['CUSTO_FIXO', 'DESPESA_FIXA', 'FINANCEIRO', 'DEDUCAO_RECEITA', 'CAIXA_NAO_RESULTADO'] as const;
export const classeRubrica = z.enum(CLASSES_RUBRICA);
export type ClasseRubrica = z.infer<typeof classeRubrica>;

export const criarRubricaSchema = z.object({ nome: z.string().trim().min(1).max(80), classe: classeRubrica, ordem: z.number().int().min(0).max(999).default(0) });
export type CriarRubricaDto = z.infer<typeof criarRubricaSchema>;
export const atualizarRubricaSchema = z
  .object({ nome: z.string().trim().min(1).max(80), classe: classeRubrica, ordem: z.number().int().min(0).max(999), ativa: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarRubricaDto = z.infer<typeof atualizarRubricaSchema>;
export interface RubricaResponse {
  id: string;
  nome: string;
  classe: ClasseRubrica;
  ordem: number;
  ativa: boolean;
}

/** Previsto do mes por rubrica (uma linha por rubrica ativa). `valor` null = ainda nao lancado (o mes pode copiar de outro). */
export const salvarPrevistoSchema = z.object({
  linhas: z.array(z.object({ rubricaId: cuid, valor: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/, 'Valor inválido (até 2 casas)').nullable(), dataPagamento: isoDate.nullish() })).max(200),
});
export type SalvarPrevistoDto = z.infer<typeof salvarPrevistoSchema>;
export const copiarPrevistoSchema = z.object({ origem: competencia });
export type CopiarPrevistoDto = z.infer<typeof copiarPrevistoSchema>;
export interface PrevistoLinhaResponse {
  rubricaId: string;
  nome: string;
  classe: ClasseRubrica;
  valor: string | null;
  dataPagamento: string | null;
}
export interface PrevistoMesResponse {
  competencia: string;
  linhas: PrevistoLinhaResponse[];
  /** Soma de TODAS as rubricas (como a planilha: o "custo fabrica" do mes). */
  total: string;
}

// ---------- Carteira (pedidos de venda) ----------

export const STATUS_PEDIDO = ['CONCLUIDO', 'CONFIRMADO', 'PREVISAO', 'PENDENTE'] as const;
export const statusPedido = z.enum(STATUS_PEDIDO);
export type StatusPedido = z.infer<typeof statusPedido>;
/** Entram no total do mes: Concluido, Confirmado e Previsao (com o peso da Previsao). Pendente fica fora. */
export const STATUS_NO_TOTAL: readonly StatusPedido[] = ['CONCLUIDO', 'CONFIRMADO', 'PREVISAO'];

export const modalidadeFrete = z.enum(['CIF', 'FOB']);
export type ModalidadeFrete = z.infer<typeof modalidadeFrete>;

export const parcelaPedidoSchema = z.object({ dias: z.number().int().min(0).max(720), pct: taxa });
export type ParcelaPedidoDto = z.infer<typeof parcelaPedidoSchema>;

const pedidoBase = z.object({
  clienteId: cuid,
  itemId: cuid,
  /** Na unidade de CUSTO do item (t). */
  quantidade: dinheiro,
  /** R$ por unidade de VENDA (kg). */
  precoUnitario: dinheiro,
  dataEmbarque: isoDate,
  previsaoEntrega: isoDate.nullish(),
  status: statusPedido,
  numeroPedidoCliente: z.string().trim().max(60).nullish(),
  nf: z.string().trim().max(60).nullish(),
  transportadora: z.string().trim().max(120).nullish(),
  modalidadeFrete: modalidadeFrete.nullish(),
  /** Frete total do pedido OU R$ por unidade de custo (t); o total tem prioridade. */
  freteTotal: dinheiro.nullish(),
  fretePorUnidade: dinheiro.nullish(),
  /** A transportadora credita ICMS/PIS/COFINS? (Simples e pessoa fisica: nao). Padrao sim. */
  transportadoraCredita: z.boolean().optional(),
  /** Frete net informado (importacao/planilha); ausente = a API calcula pela regra do plano. */
  freteNet: dinheiro.nullish(),
  vendedorId: cuid.nullish(),
  vendedorNome: z.string().trim().max(120).nullish(),
  /** Aliquotas do pedido (fracao); ausentes = padrao do plano. */
  comissaoPct: taxa.nullish(),
  icmsPct: taxa.nullish(),
  pisPct: taxa.nullish(),
  cofinsPct: taxa.nullish(),
  condicaoPagamento: z.string().trim().max(200).nullish(),
  /** Parcelas explicitas; ausentes = lidas do texto da condicao (ex.: "30 DDL"). */
  parcelas: z.array(parcelaPedidoSchema).max(24).optional(),
  observacao: z.string().trim().max(500).nullish(),
  /** Custo por unidade de custo (t); ausente = o da ficha escolhida do item na data de embarque. */
  custoPorUnidade: dinheiro.nullish(),
  /** Sem custo (nem ficha/aquisicao nem custo informado): o pedido entra marcado "sem custo", fora da MC e do resultado do mes. Nunca vira custo zero. */
  semCusto: z.boolean().optional(),
  orcamentoId: cuid.nullish(),
});
export const criarPedidoSchema = pedidoBase;
export type CriarPedidoDto = z.infer<typeof criarPedidoSchema>;
export const atualizarPedidoSchema = pedidoBase.partial().refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarPedidoDto = z.infer<typeof atualizarPedidoSchema>;

export const listarPedidosSchema = paginacao.extend({
  mes: competencia.optional(),
  /** Periodo de embarque (alternativa ao mes; usado pela API de integracao). */
  de: isoDate.optional(),
  ate: isoDate.optional(),
  status: statusPedido.optional(),
  clienteId: cuid.optional(),
  itemId: cuid.optional(),
  q: z.string().trim().min(1).max(100).optional(),
});
export type ListarPedidosQuery = z.infer<typeof listarPedidosSchema>;

export const repetirPedidoSchema = z.object({ meses: z.number().int().min(1).max(24) });
export type RepetirPedidoDto = z.infer<typeof repetirPedidoSchema>;

/** "Gerar pedido" a partir de um orcamento aprovado: um pedido por item, na data de embarque informada. */
export const gerarPedidoDeOrcamentoSchema = z.object({ dataEmbarque: isoDate, status: statusPedido.default('PREVISAO'), previsaoEntrega: isoDate.nullish() });
export type GerarPedidoDeOrcamentoDto = z.infer<typeof gerarPedidoDeOrcamentoSchema>;

export interface PedidoVendaResponse {
  id: string;
  numero: number;
  clienteId: string;
  clienteNome: string;
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  orcamentoId: string | null;
  recorrenciaId: string | null;
  quantidade: string;
  dataEmbarque: string;
  previsaoEntrega: string | null;
  status: StatusPedido;
  numeroPedidoCliente: string | null;
  nf: string | null;
  transportadora: string | null;
  modalidadeFrete: ModalidadeFrete | null;
  vendedorId: string | null;
  vendedorNome: string | null;
  condicaoPagamento: string | null;
  observacao: string | null;
  /** Sem custo na data: fora da MC e do resultado (mc/custo ausentes). */
  semCusto: boolean;
  /** Com preco.ver (a chave nem existe sem a permissao). */
  precoUnitario?: string;
  total?: string;
  comissaoPct?: string;
  icmsPct?: string;
  pisPct?: string;
  cofinsPct?: string;
  freteTotal?: string;
  parcelas?: ParcelaPedidoDto[];
  /** Com custo.ver. */
  freteNet?: string;
  custoPorUnidade?: string;
  custoOrigem?: 'FICHA' | 'IMPORTADO' | 'MANUAL' | 'SEM_CUSTO';
  mc?: string;
  mcPorUnidade?: string | null;
}
export interface PedidosListaResponse {
  itens: PedidoVendaResponse[];
  total: number;
  /** Toneladas do filtro (todas as paginas). Sem R$. */
  somaQuantidade: string;
}

export const resumoMesSchema = z.object({ mes: competencia });
export type ResumoMesQuery = z.infer<typeof resumoMesSchema>;
export interface ResumoStatusLinha {
  status: StatusPedido;
  pedidos: number;
  quantidade: string;
  /** Com preco.ver / custo.ver. */
  faturamento?: string;
  mc?: string;
}
export interface ResumoClienteLinha {
  clienteId: string;
  clienteNome: string;
  pedidos: number;
  quantidade: string;
  faturamento?: string;
  mc?: string;
}
export interface ResumoMesResponse {
  mes: string;
  pesoPrevisao: string;
  porStatus: ResumoStatusLinha[];
  /** Concluido + Confirmado + Previsao (x peso). Pendente fora. */
  quantidade: string;
  /** Pedidos do total (Concluido/Confirmado/Previsao) SEM custo: ficam fora da MC e do resultado; faturamento e toneladas contam. */
  pedidosSemCusto: number;
  quantidadeSemCusto: string;
  faturamento?: string;
  mc?: string;
  /** MC por t sobre as toneladas COM custo. */
  mcPorUnidade?: string | null;
  /** Custo fabrica previsto do mes (soma das rubricas, como a planilha) e o resultado dos dois jeitos. Exigem plano.ver + custo.ver. */
  custoFabrica?: string;
  /** Como a planilha: MC - custo fabrica (todas as rubricas, inclusive as de caixa). */
  resultadoPlanilha?: string;
  /** Gerencial: MC - custo e despesa fixos - financeiro - deducoes; as rubricas de CAIXA_NAO_RESULTADO saem (vao so para o fluxo). */
  resultadoGerencial?: string;
  rubricasDeCaixa?: string;
  porCliente: ResumoClienteLinha[];
}

export interface CalendarioDia {
  data: string;
  /** 1 = segunda ... 7 = domingo. */
  diaSemana: number;
  pedidos: Array<{ id: string; numero: number; clienteNome: string; itemCodigo: string; quantidade: string; status: StatusPedido }>;
  quantidade: string;
}
export interface CalendarioSemana {
  /** Semana ISO. */
  semana: number;
  dias: CalendarioDia[];
  quantidade: string;
  /** So com preco.ver. */
  faturamento?: string;
}
export interface CalendarioResponse {
  mes: string;
  semanas: CalendarioSemana[];
}

// ---------- Compras de materia-prima ----------

export const STATUS_ENTREGA_COMPRA = ['A_ENTREGAR', 'PREVISTO', 'ENTREGUE', 'REPROGRAMAR'] as const;
export const statusEntregaCompra = z.enum(STATUS_ENTREGA_COMPRA);
export type StatusEntregaCompra = z.infer<typeof statusEntregaCompra>;
export const statusPagamentoCompra = z.enum(['A_PAGAR', 'PAGO']);
export type StatusPagamentoCompra = z.infer<typeof statusPagamentoCompra>;

/** Sem dados bancarios: banco, agencia, conta, PIX e e-mails nao existem neste cadastro (e o importador os descarta na leitura). */
const compraBase = z.object({
  numeroPedido: z.string().trim().max(60).nullish(),
  nf: z.string().trim().max(60).nullish(),
  emissao: isoDate.nullish(),
  fornecedorId: cuid.nullish(),
  itemId: cuid,
  /** Na unidade de CUSTO do item (t). */
  quantidade: dinheiro,
  /** R$ por unidade de VENDA/compra (kg): total = preco x fator x quantidade. */
  precoUnitario: dinheiro,
  dataEntrega: isoDate.nullish(),
  condicaoPagamento: z.string().trim().max(200).nullish(),
  dataPagamento: isoDate.nullish(),
  statusEntrega: statusEntregaCompra.default('A_ENTREGAR'),
  statusPagamento: statusPagamentoCompra.default('A_PAGAR'),
  tipoProduto: z.string().trim().max(60).nullish(),
  observacao: z.string().trim().max(500).nullish(),
});
export const criarCompraSchema = compraBase;
export type CriarCompraDto = z.infer<typeof criarCompraSchema>;
export const atualizarCompraSchema = compraBase.partial().refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarCompraDto = z.infer<typeof atualizarCompraSchema>;
export const listarComprasSchema = paginacao.extend({
  mes: competencia.optional(),
  de: isoDate.optional(),
  ate: isoDate.optional(),
  statusEntrega: statusEntregaCompra.optional(),
  statusPagamento: statusPagamentoCompra.optional(),
  fornecedorId: cuid.optional(),
  itemId: cuid.optional(),
});
export type ListarComprasQuery = z.infer<typeof listarComprasSchema>;
export interface CompraMpResponse {
  id: string;
  numeroPedido: string | null;
  nf: string | null;
  emissao: string | null;
  fornecedorId: string | null;
  fornecedorNome: string | null;
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  quantidade: string;
  precoUnitario: string;
  total: string;
  dataEntrega: string | null;
  condicaoPagamento: string | null;
  dataPagamento: string | null;
  statusEntrega: StatusEntregaCompra;
  statusPagamento: StatusPagamentoCompra;
  tipoProduto: string | null;
  observacao: string | null;
}
export interface ComprasListaResponse {
  itens: CompraMpResponse[];
  total: number;
  somaQuantidade: string;
  somaTotal: string;
}
export interface CalendarioRecebimentoDia {
  data: string;
  diaSemana: number;
  compras: Array<{ id: string; fornecedorNome: string | null; itemCodigo: string; quantidade: string; statusEntrega: StatusEntregaCompra }>;
  quantidade: string;
}
export interface CalendarioRecebimentoResponse {
  mes: string;
  semanas: Array<{ semana: number; dias: CalendarioRecebimentoDia[]; quantidade: string }>;
}

// ---------- Necessidade de MP ----------

export const necessidadeQuerySchema = z.object({ de: isoDate, ate: isoDate, incluirPendente: z.enum(['true', 'false']).transform((v) => v === 'true').optional() });
export type NecessidadeQuery = z.infer<typeof necessidadeQuerySchema>;
export interface NecessidadeSemana {
  /** Segunda-feira da semana (ISO). */
  inicio: string;
  semana: number;
  necessidade: string;
  compras: string;
  /** max(0, necessidade - compras): o que ainda falta comprar. */
  aComprar: string;
}
export interface NecessidadeItem {
  itemId: string;
  codigo: string;
  descricao: string;
  semanas: NecessidadeSemana[];
  necessidade: string;
  compras: string;
  aComprar: string;
}
export interface NecessidadeResponse {
  de: string;
  ate: string;
  incluiPendente: boolean;
  /** Pedidos sem ficha ou com erro de custo na data: ficam fora da conta (e avisados). */
  semFicha: Array<{ pedidoId: string; numero: number; itemCodigo: string; motivo: string }>;
  itens: NecessidadeItem[];
}

// ---------- Fluxo de caixa projetado ----------

export const fluxoQuerySchema = z.object({ de: isoDate, ate: isoDate, granularidade: z.enum(['semana', 'mes']).default('semana') });
export type FluxoQuery = z.infer<typeof fluxoQuerySchema>;
export interface FluxoPeriodo {
  inicio: string;
  fim: string;
  entradas: string;
  compras: string;
  rubricas: string;
  comissoes: string;
  saidas: string;
  saldoPeriodo: string;
  saldoAcumulado: string;
  /** Saldo acumulado negativo no fim do periodo. */
  alerta: boolean;
}
export interface FluxoResponse {
  de: string;
  ate: string;
  granularidade: 'semana' | 'mes';
  saldoInicial: string;
  periodos: FluxoPeriodo[];
  /** Primeiro periodo com saldo negativo (null = nunca). */
  primeiroNegativo: string | null;
}

// ---------- Tokens de integracao (API do plano) ----------

export const criarTokenIntegracaoSchema = z.object({ nome: z.string().trim().min(1).max(80) });
export type CriarTokenIntegracaoDto = z.infer<typeof criarTokenIntegracaoSchema>;
export interface TokenIntegracaoResponse {
  id: string;
  nome: string;
  prefixo: string;
  escopo: 'LEITURA';
  criadoEm: string;
  ultimoUsoEm: string | null;
  revogadoEm: string | null;
}
/** O valor do token so existe nesta resposta (a API guarda apenas o hash). */
export interface TokenIntegracaoCriadoResponse extends TokenIntegracaoResponse {
  token: string;
}

/** Filtros da API de integracao (somente leitura): periodo e paginacao. */
export const integracaoQuerySchema = paginacao.extend({ de: isoDate.optional(), ate: isoDate.optional() });
export type IntegracaoQuery = z.infer<typeof integracaoQuerySchema>;
